/**
 * Carbonômetro — painel agregado de emissões pra exibição em TVs/projeções
 * durante eventos. Mockup 16 (D12).
 *
 * Snapshot via `GET /v1/public/carbonometer/{token}` (mesma auth do widget
 * embed — não autenticado).
 *
 * MVP: polling a cada 10s pra atualizar contadores. v1.1+ pode trocar
 * pra WebSocket (Redis Pub/Sub) com latência <500ms (ver banner mockup 16).
 *
 * Layout:
 *  - Background image full-screen (theme_image_url ou paleta OMA)
 *  - Card central com:
 *    · eyebrow "Emissões de GEE acumuladas · ao vivo"
 *    · contador grande tCO₂e (count-up animado entre snapshots)
 *    · sub "X submissões · Y dias de evento"
 *    · barras horizontais top categorias com %
 *    · footer com token + branding OMA
 */
import { useEffect, useRef, useState } from 'react';
import { CalculatorClient } from './api/client';
import type { CarbonometerSnapshot } from './api/types';

const POLL_INTERVAL_MS = 10_000;
const COUNTUP_DURATION_MS = 2400;
// Janela em ms da classe `.pulse` no stage — sincronizada com os
// keyframes oma-flash/oma-ring no globals.css.
const PULSE_DURATION_MS = 2200;

export interface CarbonometerWidgetProps {
  token: string;
  apiBase: string;
  locale?: string;
  /** Override do interval de polling (testes). Default 10s. */
  pollIntervalMs?: number;
}

export function CarbonometerWidget({ token, apiBase, locale, pollIntervalMs }: CarbonometerWidgetProps) {
  const clientRef = useRef<CalculatorClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = new CalculatorClient({ baseUrl: apiBase, locale });
  }

  const [snapshot, setSnapshot] = useState<CarbonometerSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [displayedTotal, setDisplayedTotal] = useState(0);
  const previousTotal = useRef(0);
  // `pulse` ativa a animação de "nova emissão chegou" (ring + glow + flash).
  // Dura PULSE_DURATION_MS e é re-disparada toda vez que o total cresce.
  const [pulse, setPulse] = useState(false);
  // `delta` mostra "+X tCO₂e" temporariamente acima do contador.
  const [delta, setDelta] = useState<number | null>(null);

  // Polling loop. Re-fetcha a cada `POLL_INTERVAL_MS` (10s default).
  useEffect(() => {
    let cancelled = false;
    const interval = pollIntervalMs ?? POLL_INTERVAL_MS;

    const tick = async () => {
      try {
        const data = await clientRef.current!.getCarbonometer(token);
        if (cancelled) return;
        setSnapshot(data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message ?? 'Erro de rede');
      }
    };

    void tick(); // primeiro fetch imediato
    const handle = window.setInterval(tick, interval);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [token, pollIntervalMs]);

  // Count-up animado quando o total muda (entre polls).
  // Quando há INCREMENTO real, dispara o pulse — flash no card + ring
  // expansor + glow no counter + delta badge "+X tCO₂e" acima do número.
  useEffect(() => {
    if (!snapshot) return;
    const from = previousTotal.current;
    const to = snapshot.total_tco2e;
    if (from === to) {
      setDisplayedTotal(to);
      return;
    }

    // Pulse só quando o total CRESCE (boot inicial não conta — from=0 vai
    // direto pro valor sem flash dramático).
    const isIncrement = from > 0 && to > from;
    if (isIncrement) {
      setDelta(to - from);
      setPulse(true);
    }

    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / COUNTUP_DURATION_MS);
      // ease-out cubic — desacelera suave no final, dá sensação de "pousar".
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayedTotal(from + (to - from) * eased);
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        previousTotal.current = to;
      }
    };
    raf = requestAnimationFrame(step);

    // Limpa pulse após PULSE_DURATION_MS pra preparar pro próximo evento.
    let pulseTimer: number | undefined;
    let deltaTimer: number | undefined;
    if (isIncrement) {
      pulseTimer = window.setTimeout(() => setPulse(false), PULSE_DURATION_MS);
      deltaTimer = window.setTimeout(() => setDelta(null), PULSE_DURATION_MS + 800);
    }

    return () => {
      cancelAnimationFrame(raf);
      if (pulseTimer !== undefined) window.clearTimeout(pulseTimer);
      if (deltaTimer !== undefined) window.clearTimeout(deltaTimer);
    };
  }, [snapshot]);

  // Estado de erro inicial (sem snapshot ainda).
  if (error && !snapshot) {
    return (
      <div className="oma-carbonometer-stage error">
        <div className="oma-carbonometer-card">
          <p className="text-sm text-cobre-700">
            Não foi possível carregar o carbonômetro.
            <br />
            <span className="text-xs text-tabaco font-mono">{error}</span>
          </p>
        </div>
      </div>
    );
  }
  if (!snapshot) {
    return (
      <div className="oma-carbonometer-stage">
        <div className="oma-carbonometer-card">
          <div className="size-8 rounded-full border-2 border-floresta/20 border-t-floresta animate-spin" />
        </div>
      </div>
    );
  }

  const bgStyle: React.CSSProperties = snapshot.theme_image_url
    ? {
        backgroundImage: `linear-gradient(rgba(41,50,62,0.25), rgba(74,76,57,0.30)), url(${snapshot.theme_image_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { background: 'linear-gradient(180deg, #4A4C39 0%, #29323E 100%)' };

  const eventLabel = snapshot.label?.trim() || snapshot.event_city || '';
  // Total de dias do evento (period_end - period_start), em dias inteiros.
  // Pareado com event_days_elapsed pra mostrar "Dia X de Y".
  const totalDays = Math.max(
    1,
    Math.ceil(
      (new Date(snapshot.period_end).getTime() - new Date(snapshot.period_start).getTime()) /
        (1000 * 60 * 60 * 24),
    ),
  );

  return (
    <div
      className={'oma-carbonometer-stage' + (pulse ? ' pulse' : '')}
      style={bgStyle}
    >
      {/* Anéis expansores que disparam no pulse — 2 rings com delays
        diferentes pra dar sensação de "onda" saindo do counter. */}
      {pulse && (
        <>
          <span className="cg-ring r1" aria-hidden />
          <span className="cg-ring r2" aria-hidden />
        </>
      )}

      <div className="oma-carbonometer-card">
        <div className="cg-eyebrow">
          <span className="live-dot" />
          Emissões de GEE acumuladas · ao vivo
        </div>

        <div className="cg-counter-wrap">
          {delta !== null && delta > 0 && (
            <span className="cg-delta" aria-hidden>
              +{formatCounter(delta)} tCO₂e
            </span>
          )}
          <div className="cg-counter">
            {formatCounter(displayedTotal)}
            <span className="cg-unit">tCO₂e</span>
          </div>
        </div>

        <p className="cg-sub">
          calculadas em <strong>{snapshot.submission_count.toLocaleString('pt-BR')}</strong>{' '}
          {snapshot.submission_count === 1 ? 'submissão' : 'submissões'}
          {snapshot.event_days_elapsed > 0 && (
            <>
              {' '}·{' '}
              <strong>
                Dia {snapshot.event_days_elapsed} de {totalDays}
              </strong>
            </>
          )}
        </p>

        <div className="cg-divider" />

        <div className="cg-foot">
          <span>OMA · Ativos Ambientais</span>
          {eventLabel && <span>{eventLabel}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * Formata número com vírgula decimal e separador de milhar (pt-BR).
 * Mostra 2 casas pra <100 e 1 casa pra >=100 — equilibra precisão com
 * legibilidade em TV (números grandes).
 */
function formatCounter(v: number): string {
  const decimals = v < 100 ? 2 : v < 1000 ? 1 : 0;
  return v.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
